import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const SCROLL_AFFORDANCE_THRESHOLD = 8;

const syncMenuScrollAffordances = (root = document) => {
  const scrollMenus = root.querySelectorAll('[data-scroll-menu]');

  scrollMenus.forEach((scrollMenu) => {
    if (!(scrollMenu instanceof HTMLElement)) return;

    const submenu = scrollMenu.closest('.menu-list__submenu');
    const button = submenu?.querySelector('[data-scroll-menu-button]');
    if (!(button instanceof HTMLButtonElement)) return;

    const hasOverflow = scrollMenu.scrollHeight - scrollMenu.clientHeight > SCROLL_AFFORDANCE_THRESHOLD;
    const hasMoreBelow =
      scrollMenu.scrollTop + scrollMenu.clientHeight < scrollMenu.scrollHeight - SCROLL_AFFORDANCE_THRESHOLD;
    const isVisible = hasOverflow && hasMoreBelow;

    button.hidden = !isVisible;
    button.classList.toggle('is-visible', isVisible);
    button.setAttribute('aria-hidden', (!isVisible).toString());
  });
};

/**
 * @param {Event} event
 */
const handleMenuScrollAffordanceClick = (event) => {
  if (event.defaultPrevented || !(event.target instanceof Element)) return;

  const button = event.target.closest('[data-scroll-menu-button]');
  if (!(button instanceof HTMLButtonElement)) return;

  const submenu = button.closest('.menu-list__submenu');
  const scrollMenu = submenu?.querySelector('[data-scroll-menu]');
  if (!(scrollMenu instanceof HTMLElement)) return;

  event.preventDefault();
  scrollMenu.scrollBy({
    top: Math.max(scrollMenu.clientHeight * 0.7, 160),
    behavior: 'smooth',
  });

  requestAnimationFrame(() => syncMenuScrollAffordances(submenu ?? document));
};

const scheduleMenuScrollAffordanceSync = () => {
  requestAnimationFrame(() => syncMenuScrollAffordances());
};

const initMenuScrollAffordances = () => {
  syncMenuScrollAffordances();
  requestAnimationFrame(() => syncMenuScrollAffordances());
  setTimeout(() => syncMenuScrollAffordances(), 250);
  setTimeout(() => syncMenuScrollAffordances(), 1000);

  document.addEventListener('click', handleMenuScrollAffordanceClick);
  document.addEventListener('pointermove', scheduleMenuScrollAffordanceSync, { passive: true });
  document.addEventListener('scroll', scheduleMenuScrollAffordanceSync, { capture: true, passive: true });
  window.addEventListener('resize', scheduleMenuScrollAffordanceSync, { passive: true });
};

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('click', this.#handleMegaMenuTabInteraction, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleBrandMenuInteraction, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleBrandSubmenuInteraction, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleScrollAffordanceClick, { signal: this.#abortController.signal });
    this.addEventListener('focusin', this.#handleBrandMenuInteraction, { signal: this.#abortController.signal });
    this.addEventListener('pointerenter', this.#handleMegaMenuTabInteraction, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('keydown', this.#handleMegaMenuTabKeydown, { signal: this.#abortController.signal });
    this.addEventListener('scroll', this.#handleScrollMenuScroll, {
      capture: true,
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
    requestAnimationFrame(() => this.#syncScrollAffordances());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    requestAnimationFrame(() => this.#syncScrollAffordances(submenu ?? this));
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Switch brand detail panels in the Brands navigation hub.
   * @param {Event} event
   */
  #handleBrandMenuInteraction = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-brand-menu-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    this.#activateBrandMenuPanel(trigger);
  };

  /**
   * @param {HTMLButtonElement} activeTrigger
   */
  #activateBrandMenuPanel(activeTrigger) {
    const brandHub = activeTrigger.closest('.navigation-hub--brands');
    if (!(brandHub instanceof HTMLElement)) return;

    const selectedPanelId = activeTrigger.dataset.brandMenuTrigger;
    if (!selectedPanelId) return;

    const triggers = brandHub.querySelectorAll('[data-brand-menu-trigger]');
    const panels = brandHub.querySelectorAll('[data-brand-menu-panel]');

    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement)) return;

      const isSelected = trigger === activeTrigger;
      trigger.classList.toggle('is-active', isSelected);
      trigger.setAttribute('aria-selected', isSelected.toString());
    });

    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const isSelected = panel.dataset.brandMenuPanel === selectedPanelId;

      if (isSelected) {
        panel.hidden = false;
        this.#resetBrandSubmenus(panel);
        requestAnimationFrame(() => {
          panel.classList.add('is-active');
          this.#syncSubmenuHeight();
          this.#syncScrollAffordances(panel.closest('.menu-list__submenu') ?? panel);
        });
        return;
      }

      panel.classList.remove('is-active');
      setTimeout(() => {
        if (!panel.classList.contains('is-active')) {
          panel.hidden = true;
        }
      }, 220);
    });
  }

  /**
   * Toggle category accordions inside a brand detail panel.
   * @param {Event} event
   */
  #handleBrandSubmenuInteraction = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-brand-submenu-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    this.#toggleBrandSubmenu(toggle);
  };

  /**
   * @param {HTMLButtonElement} activeToggle
   */
  #toggleBrandSubmenu(activeToggle) {
    const panel = activeToggle.closest('[data-brand-menu-panel]');
    const item = activeToggle.closest('.navigation-hub__brand-submenu-item');
    const contentId = activeToggle.dataset.brandSubmenuToggle;
    const content = contentId ? panel?.querySelector(`[data-brand-submenu-content="${contentId}"]`) : null;

    if (!(panel instanceof HTMLElement) || !(item instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    const shouldOpen = !item.classList.contains('is-open');

    panel.querySelectorAll('.navigation-hub__brand-submenu-item.is-open').forEach((openItem) => {
      if (openItem instanceof HTMLElement && openItem !== item) {
        this.#closeBrandSubmenu(openItem);
      }
    });

    if (shouldOpen) {
      this.#openBrandSubmenu(item, activeToggle, content);
      return;
    }

    this.#closeBrandSubmenu(item);
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLButtonElement} toggle
   * @param {HTMLElement} content
   */
  #openBrandSubmenu(item, toggle, content) {
    item.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    content.hidden = false;
    content.style.maxHeight = '0px';

    requestAnimationFrame(() => {
      content.classList.add('is-open');
      content.style.maxHeight = `${content.scrollHeight}px`;
      this.#syncSubmenuHeight();
      this.#syncScrollAffordances(content.closest('.menu-list__submenu') ?? content);
    });

    setTimeout(() => {
      this.#syncSubmenuHeight();
      this.#syncScrollAffordances(content.closest('.menu-list__submenu') ?? content);
    }, 220);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} [immediate]
   */
  #closeBrandSubmenu(item, immediate = false) {
    const toggle = item.querySelector('[data-brand-submenu-toggle]');
    const content = item.querySelector('[data-brand-submenu-content]');

    if (!(toggle instanceof HTMLButtonElement) || !(content instanceof HTMLElement)) return;

    item.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    content.classList.remove('is-open');
    content.style.maxHeight = '0px';

    if (immediate) {
      content.hidden = true;
      this.#syncSubmenuHeight();
      this.#syncScrollAffordances(content.closest('.menu-list__submenu') ?? content);
      return;
    }

    setTimeout(() => {
      if (!item.classList.contains('is-open')) {
        content.hidden = true;
        this.#syncSubmenuHeight();
        this.#syncScrollAffordances(content.closest('.menu-list__submenu') ?? content);
      }
    }, 220);
  }

  /**
   * @param {HTMLElement} panel
   */
  #resetBrandSubmenus(panel) {
    panel.querySelectorAll('.navigation-hub__brand-submenu-item.is-open').forEach((item) => {
      if (item instanceof HTMLElement) {
        this.#closeBrandSubmenu(item, true);
      }
    });
  }

  /**
   * Switch tabbed mega-menu panels without closing the parent submenu.
   * @param {Event} event
   */
  #handleMegaMenuTabInteraction = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-mega-menu-tab]');
    if (!(tab instanceof HTMLButtonElement)) return;

    this.#activateMegaMenuTab(tab);
  };

  /**
   * Provide keyboard navigation for tabbed mega menus.
   * @param {KeyboardEvent} event
   */
  #handleMegaMenuTabKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-mega-menu-tab]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;

    const tabs = [...tablist.querySelectorAll('[data-mega-menu-tab]')].filter(
      (element) => element instanceof HTMLButtonElement
    );
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();

    const nextTab = tabs[nextIndex];
    if (nextTab instanceof HTMLButtonElement) {
      this.#activateMegaMenuTab(nextTab);
      nextTab.focus();
    }
  };

  /**
   * @param {HTMLButtonElement} activeTab
   */
  #activateMegaMenuTab(activeTab) {
    const tabsRoot = activeTab.closest('[data-mega-menu-tabs]');
    if (!(tabsRoot instanceof HTMLElement)) return;

    const selectedTabKey = activeTab.dataset.megaMenuTab;
    if (!selectedTabKey) return;

    const tabs = tabsRoot.querySelectorAll('[data-mega-menu-tab]');
    const panels = tabsRoot.querySelectorAll('[role="tabpanel"]');

    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLButtonElement)) return;

      const isSelected = tab === activeTab;
      tab.classList.toggle('is-active', isSelected);
      tab.setAttribute('aria-selected', isSelected.toString());
      tab.tabIndex = isSelected ? 0 : -1;
    });

    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const isSelected = panel.id === activeTab.getAttribute('aria-controls');
      panel.classList.toggle('is-active', isSelected);
      panel.hidden = !isSelected;
    });

    requestAnimationFrame(() => this.#syncSubmenuHeight());
    requestAnimationFrame(() => this.#syncScrollAffordances(tabsRoot.closest('.menu-list__submenu') ?? tabsRoot));
  }

  #syncSubmenuHeight() {
    let submenu = findSubmenu(this.#state.activeItem);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && this.dataset.overflowExpanded === 'true') {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    if (submenuHeight > 0) {
      this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    }
  }

  /**
   * Scroll the currently visible menu panel when the down affordance is clicked.
   * @param {Event} event
   */
  #handleScrollAffordanceClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-scroll-menu-button]');
    if (!(button instanceof HTMLButtonElement)) return;

    const submenu = button.closest('.menu-list__submenu');
    const scrollMenu = submenu?.querySelector('[data-scroll-menu]');
    if (!(scrollMenu instanceof HTMLElement)) return;

    scrollMenu.scrollBy({
      top: Math.max(scrollMenu.clientHeight * 0.7, 160),
      behavior: 'smooth',
    });

    event.preventDefault();
    requestAnimationFrame(() => this.#syncScrollAffordances(submenu));
  };

  /**
   * Keep affordance visibility in sync with manual scrolling.
   * @param {Event} event
   */
  #handleScrollMenuScroll = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-scroll-menu]')) return;

    this.#syncScrollAffordances(event.target.closest('.menu-list__submenu') ?? event.target);
  };

  /**
   * Show the down affordance only while a menu has hidden content below.
   * @param {Element} [root]
   */
  #syncScrollAffordances(root = this) {
    syncMenuScrollAffordances(root);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMenuScrollAffordances, { once: true });
} else {
  initMenuScrollAffordances();
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  const listItem = element.closest('.menu-list__list-item');
  const menuItem = listItem?.querySelector('[ref="menuitem"]') ?? element.querySelector('[ref="menuitem"]');

  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
